import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MEDIA_MAX_BYTES, type MediaAsset } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, Skeleton, TextField, useToast } from '@agency/ui';
import { ApiClientError, get, patch, post, upload } from '../../lib/api.ts';
import { fmtDate } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { useAuth } from '../../lib/auth.tsx';
import './media.css';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const size = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function useMediaList(filter: { q?: string; tag?: string; archived?: boolean; missingAlt?: boolean }, enabled = true) {
  const qs = new URLSearchParams();
  if (filter.q) qs.set('q', filter.q);
  if (filter.tag) qs.set('tag', filter.tag);
  if (filter.archived) qs.set('archived', 'true');
  if (filter.missingAlt) qs.set('missingAlt', 'true');
  return useQuery<MediaAsset[], ApiClientError>({ queryKey: ['media', qs.toString()], queryFn: () => get(`/admin/media?${qs}`), enabled });
}

/** Uploads one or more files sequentially and reports each outcome; failures never hide earlier successes. */
export function useUpload(onUploaded?: (a: MediaAsset) => void) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  async function run(files: File[]) {
    setBusy(true); setErrors([]);
    const errs: string[] = [];
    for (const f of files) {
      if (f.size > MEDIA_MAX_BYTES) { errs.push(`${f.name}: larger than ${MEDIA_MAX_BYTES / 1024 / 1024} MB`); continue; }
      try {
        const r = await upload<{ asset: MediaAsset; duplicate: boolean }>('/admin/media/upload', f);
        toast({ tone: r.duplicate ? 'info' : 'success', message: r.duplicate ? `${f.name} was already in the library.` : `${f.name} uploaded. Add alt text before using it on a page.` });
        onUploaded?.(r.asset);
      } catch (e) { errs.push(`${f.name}: ${(e as ApiClientError).message}`); }
    }
    setErrors(errs); setBusy(false);
    void qc.invalidateQueries({ queryKey: ['media'] });
  }
  return { run, busy, errors };
}

function UploadZone({ onFiles, busy, errors }: { onFiles: (f: File[]) => void; busy: boolean; errors: string[] }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div>
      <div className="dropzone" data-over={over || undefined} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); onFiles([...e.dataTransfer.files]); }}>
        <p style={{ margin: 0 }}>Drop images here, or</p>
        <Button icon="plus" loading={busy} onClick={() => input.current?.click()}>Choose images</Button>
        <input ref={input} type="file" accept={ACCEPT} multiple className="ag-visually-hidden" aria-label="Choose images to upload" tabIndex={-1} onChange={(e) => { onFiles([...(e.target.files ?? [])]); e.target.value = ''; }} />
        <p className="ag-hint" style={{ margin: 0 }}>PNG, JPEG, WebP or GIF up to {MEDIA_MAX_BYTES / 1024 / 1024} MB. SVG is not accepted.</p>
      </div>
      {errors.length ? <div style={{ marginTop: 8 }}><Notice tone="error" title="Some files were not uploaded"><ul>{errors.map((e) => <li key={e}>{e}</li>)}</ul></Notice></div> : null}
    </div>
  );
}

function Detail({ asset, onClose, canEdit }: { asset: MediaAsset | null; onClose: () => void; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [alt, setAlt] = useState<string | null>(null);
  const [tags, setTags] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const close = () => { setAlt(null); setTags(null); setName(null); onClose(); };
  const save = useMutation<MediaAsset, ApiClientError, void>({
    mutationFn: () => patch(`/admin/media/${asset!.id}`, { ...(alt !== null ? { altText: alt } : {}), ...(tags !== null ? { tags: tags.split(',').map((t) => t.trim()).filter(Boolean) } : {}), ...(name !== null ? { filename: name } : {}) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['media'] }); toast({ tone: 'success', message: 'Image details saved.' }); close(); },
  });
  const archive = useMutation<void, ApiClientError, boolean>({
    mutationFn: (a) => post(`/admin/media/${asset!.id}/${a ? 'archive' : 'restore'}`),
    onSuccess: (_r, a) => { void qc.invalidateQueries({ queryKey: ['media'] }); toast({ tone: 'success', message: a ? 'Archived. Pages that use it keep working.' : 'Restored to the library.' }); close(); },
  });
  const dirty = alt !== null || tags !== null || name !== null;
  return (
    <Drawer open={asset !== null} onClose={close} title={asset?.filename ?? 'Image'} width={480}
      footer={canEdit && asset ? <><Button variant="ghost" loading={archive.isPending} onClick={() => archive.mutate(!asset.archived)}>{asset.archived ? 'Restore' : 'Archive'}</Button><Button variant="primary" loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>Save details</Button></> : undefined}>
      {asset ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <img className="media-preview" src={`${asset.url}?w=640`} alt={asset.altText ?? ''} />
          <dl className="summary">
            <dt>Type</dt><dd>{asset.mimeType}</dd>
            <dt>Size</dt><dd>{size(asset.sizeBytes)}{asset.width ? ` · ${asset.width}×${asset.height}` : ''}</dd>
            <dt>Added</dt><dd>{fmtDate(asset.createdAt)}</dd>
            <dt>Used in</dt><dd>{asset.usageCount} draft{asset.usageCount === 1 ? '' : 's'}</dd>
            <dt>Address</dt><dd className="ag-mono" style={{ wordBreak: 'break-all' }}>{asset.url}</dd>
          </dl>
          <TextField label="Alternative text" value={alt ?? asset.altText ?? ''} readOnly={!canEdit} onChange={(e) => setAlt(e.target.value)} maxLength={200} hint="Describe what the image shows for people who cannot see it. Leave empty only for purely decorative images." />
          <TextField label="Tags" value={tags ?? asset.tags.join(', ')} readOnly={!canEdit} onChange={(e) => setTags(e.target.value)} hint="Comma separated, for example team, office" />
          <TextField label="File name" value={name ?? asset.filename.replace(/\.[^.]+$/, '')} readOnly={!canEdit} onChange={(e) => setName(e.target.value)} hint="Used in the public address. Existing pages keep working if you change it." />
          {save.isError ? <Notice tone="error" title="Could not save">{save.error.message}</Notice> : null}
          {archive.isError ? <Notice tone="error" title="Could not update">{archive.error.message}</Notice> : null}
        </div>
      ) : null}
    </Drawer>
  );
}

export function MediaGrid({ items, onOpen, onPick }: { items: MediaAsset[]; onOpen?: (a: MediaAsset) => void; onPick?: (a: MediaAsset) => void }) {
  return (
    <ul className="media-grid" aria-label="Images">
      {items.map((a) => (
        <li key={a.id}>
          <button type="button" className="media-card" onClick={() => (onPick ?? onOpen)?.(a)} aria-label={`${onPick ? 'Use' : 'Open'} ${a.filename}${a.altText ? '' : ', no alt text'}`}>
            <img src={`${a.url}?w=320`} alt="" loading="lazy" width={a.width ?? undefined} height={a.height ?? undefined} />
            <span className="media-card__name">{a.filename}</span>
            <span className="media-card__meta">{!a.altText ? <Badge tone="warning">No alt text</Badge> : null}{a.archived ? <Badge tone="neutral">Archived</Badge> : null}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function MediaPage() {
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('');
  const [archived, setArchived] = useState(false);
  const [missingAlt, setMissingAlt] = useState(false);
  const [open, setOpen] = useState<MediaAsset | null>(null);
  const list = useMediaList({ q, tag, archived, missingAlt });
  const up = useUpload();
  const canEdit = can('content.edit');
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Media</h1>
          <p>Images for pages, case studies and articles. Files are never deleted: archiving hides an image from the picker while pages that use it keep working.</p>
        </div>
      </div>
      {canEdit ? <UploadZone onFiles={(f) => void up.run(f)} busy={up.busy} errors={up.errors} /> : null}
      <form className="filters" role="search" aria-label="Filter images" onSubmit={(e) => e.preventDefault()} style={{ marginTop: 16 }}>
        <TextField label="Search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="File name" />
        <TextField label="Tag" value={tag} onChange={(e) => setTag(e.target.value)} />
        <label className="choice-inline"><input type="checkbox" checked={missingAlt} onChange={(e) => setMissingAlt(e.target.checked)} /> Missing alt text</label>
        <label className="choice-inline"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Show archived</label>
      </form>
      {list.isLoading ? <div role="status"><span className="ag-visually-hidden">Loading images…</span><Skeleton height={180} /></div>
        : list.isError ? <QueryError error={list.error} onRetry={() => void list.refetch()} />
        : list.data!.length === 0 ? <EmptyState title={q || tag || missingAlt || archived ? 'No images match' : 'No images yet'} icon="file">{canEdit ? 'Upload an image to start the library.' : 'Ask someone with edit access to add images.'}</EmptyState>
        : <MediaGrid items={list.data!} onOpen={setOpen} />}
      <Detail asset={open} onClose={() => setOpen(null)} canEdit={canEdit} />
    </>
  );
}
